import { PageOptions } from '@flakiness/server/common/pagedResponse.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ReactiveController, ReactiveControllerHost } from 'lit';
import scrollIntoView from 'scroll-into-view-if-needed';
import { Temporal } from 'temporal-polyfill';

export const HISTORY_SNIPPET_DAYS = 14;

const listenersSymbol = Symbol('listeners');

export type Disposable = () => void;
export type EventListener<T extends any[]> = (...args: T) => void;
export type SimpleEvent<T extends any[]> = Function & {
  [listenersSymbol]: Set<EventListener<T>>,
};

export function isWebUrl(string: string): boolean {
  try {
    const url = new URL(string);
    // 1. Protocol must be http: or https:
    // 2. Hostname must exist (prevents "http:foo" or "https:/" errors)
    // 3. Hostname must contain a dot (prevents localhost or single-word inputs like "http://test")
    return (url.protocol === 'http:' || url.protocol === 'https:') &&
           url.hostname.includes('.');
  } catch (err) {
    return false;
  }
}

export function renderDatesRangeZoned(since: Temporal.PlainDate, until: Temporal.PlainDate, timezone: string) {
  if (since.until(until, { largestUnit: 'day' }).days === 0) {
    const today = Temporal.Now.zonedDateTimeISO();
    if (today.year === since.year) {
      return since.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }
    return since.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const today = Temporal.Now.zonedDateTimeISO(timezone);

  // If both since and until dates from this year, then render no YEAR.
  if (since.year === today.year && until.year === today.year) {
    return [
      since.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
      until.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
    ].join(' — ');
  }

  if (since.year === until.year) {
    return [
      since.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
      until.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
    ].join(' — ') + ', ' + since.year;
  }
  return [
    since.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    until.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  ].join(' — ');
}

export async function loadAllPages<E>(pageSize: number, loadPage: (page: PageOptions) => Promise<WireTypes.PagedResponse<E>>): Promise<E[]> {
  let pageNumber = 0;
  const firstPage = await loadPage({ number: pageNumber, size: pageSize });
  const pages: WireTypes.PagedResponse<E>[] = [];
  pages.push(firstPage);
  for (let page = 1; page < firstPage.totalPages; ++page)
    pages.push(await loadPage({ number: page, size: pageSize }));
  return pages.map(page => page.elements).flat();
}

export function capitalize(word: string) {
  return word[0].toUpperCase() + word.substring(1);
}

export function onDOMEvent<E extends keyof DocumentEventMap>(target: Document, event: E, handler: (this: Document, ev: DocumentEventMap[E]) => any, capturing?: boolean): Disposable;
export function onDOMEvent<E extends keyof ElementEventMap>(target: Element, event: E, handler: (this: Element, ev: ElementEventMap[E]) => any, capturing?: boolean): Disposable;
export function onDOMEvent<E extends keyof WindowEventMap>(target: Window, event: E, handler: (this: Window, ev: WindowEventMap[E]) => any, capturing?: boolean): Disposable;
export function onDOMEvent<E extends keyof HTMLElementEventMap>(target: HTMLElement, event: E, handler: (this: HTMLElement, ev: HTMLElementEventMap[E]) => any, capturing?: boolean): Disposable;

export function onDOMEvent(target: Document|Window|Element|HTMLElement,
    event: keyof ElementEventMap | keyof WindowEventMap | keyof DocumentEventMap | keyof HTMLElementEventMap,
    handler: (this: HTMLElement | Element | Window | Document, ev: Event) => any, capturing = false): Disposable {
  target.addEventListener(event, handler, capturing);
  return () => target.removeEventListener(event, handler, capturing);
}

export function onCustomDOMEvent(target: Document|Window|Element,
    event: string,
    handler: (this: Element | Window | Document, ev: Event) => any, capturing = false): Disposable {
  target.addEventListener(event, handler, capturing);
  return () => target.removeEventListener(event, handler, capturing);
}

export function createEvent<T extends any[]>(): SimpleEvent<T> {
  const listeners = new Set<EventListener<T>>();
  const subscribeFunction: {
    (listener: EventListener<T>): Disposable;  // Main function type
    [listenersSymbol]: Set<EventListener<T>>;  // Extra property on the function
  } = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
  subscribeFunction[listenersSymbol] = listeners;
  return subscribeFunction;
}

export function emitEvent<T extends any[]>(event: SimpleEvent<T>, ...args: T) {
  /* @type {Set<function>} */
  let listeners = event[listenersSymbol];
  if (!listeners || !listeners.size)
    return;
  listeners = new Set(listeners);
  for (const listener of listeners)
    listener.call(null, ...args);
}

export function disposeAll(disposables: Disposable[]) {
  for (const d of disposables)
    d.call(null);
  disposables.splice(0);
}

export function consumeDOMEvent(event: Event) {
  event.stopPropagation();
  event.preventDefault();
}

export function preventTextSelectionOnDBLClick(element: HTMLElement) {
  // Prevent text selection on dblclick.
  element.addEventListener('mousedown', (event: MouseEvent) => {
    if (event.detail > 1)
      consumeDOMEvent(event);
  }, true);
}

export function scrollIntoViewIfNeeded(element: HTMLElement) {
  scrollIntoView(element, {
    block: 'center',
    behavior: 'instant',
    scrollMode: 'if-needed',
  });
}

/**
 * Serializing async operations one-by-one.
 */
export class CriticalSection {
  private _rollingPromises: Map<any, any>;
  constructor() {
    this._rollingPromises = new Map();
  }

  async run(key: string, operation: Function) {
    const rollingPromise = this._rollingPromises.get(key) || Promise.resolve();
    const resultPromise = rollingPromise.then(() => operation());
    const newRollingPromise = resultPromise.finally(() => {
      if (this._rollingPromises.get(key) === newRollingPromise)
        this._rollingPromises.delete(key);
    }).catch((e: any) => {/* swallow error */});
    this._rollingPromises.set(key, newRollingPromise);
    return resultPromise;
  }
}

export function deepQuerySelectorAll(selector: string, root: Document | DocumentFragment | HTMLElement | ShadowRoot = document, result: Element[] = []): Element[] {
  result.push(...root.querySelectorAll(selector));
  for (const element of root.querySelectorAll('*')) {
    if (element.shadowRoot)
      deepQuerySelectorAll(selector, element.shadowRoot, result);
  }
  return result;
}

export function deepQuerySelector(selector: string, root?: Document | DocumentFragment | HTMLElement | ShadowRoot) {
  const elements = deepQuerySelectorAll(selector, root);
  return elements.length ? elements[0] : null;
}

export class Throttler {
  private _pendingOperation: Function | null = null;
  private _runningOperation: Promise<any> | null = null;

  constructor(private _timeout = 0) {
    this._pendingOperation = null;
    this._runningOperation = null;
  }

  schedule(operation: Function) {
    this._pendingOperation = operation;
    this._maybeRun();
  }

  reset() {
    this._pendingOperation = null;
  }

  async complete() {
    await this._pendingOperation;
  }

  _maybeRun() {
    if (this._runningOperation || !this._pendingOperation)
      return;
    const operation = this._pendingOperation;
    this._pendingOperation = null;
    this._runningOperation = Promise.resolve()
        .then(() => operation.call(null))
        .catch(e => console.error(e))
        .then(() => this._timeout ? new Promise(x => setTimeout(x, this._timeout)) : undefined)
        .then(() => {
          this._runningOperation = null;
          this._maybeRun();
        });
  }
}

export class EventManager<T> implements ReactiveController {
  private _scope?: T;
  private _eventListeners: Disposable[] = [];

  constructor(host: ReactiveControllerHost, private _getScope: () => (T|undefined), private _doSubscribe: (scope: T) => Disposable[]) {
    host.addController(this);
  }

  hostDisconnected(): void {
    this._scope = undefined;
    disposeAll(this._eventListeners);
  }

  private _ensureSubscribed() {
    const scope = this._getScope();
    if (scope !== this._scope)
      disposeAll(this._eventListeners);
    this._scope = scope;
    if (this._scope)
      this._eventListeners = this._doSubscribe(this._scope);
  }

  hostConnected(): void {
    this._ensureSubscribed();
  }

  hostUpdate(): void {
    this._ensureSubscribed();
  }
}

// Helper type to transform all elements of a tuple to their non-nullable versions
type NonNullableElements<T> = {
  [K in keyof T]: NonNullable<T[K]>
};

export function iff<T extends any[], K>(
  ...args: [...T, (...args: NonNullableElements<T>) => K]
): K | undefined;
export function iff<T extends any[], K>(
  ...args: [...T, (...args: NonNullableElements<T>) => K, () => K]
): K | undefined;
export function iff<T extends any[], K>(...args: [...T, (...args: NonNullableElements<T>) => K, (() => K)?]): K|undefined {
  // Extract the function from the arguments
  let thenFn = args[args.length - 1] as (...args: NonNullableElements<T>) => K;
  let elseFn = undefined;
  
  if (args[args.length - 2] instanceof Function) {
    elseFn = thenFn as () => K;
    thenFn = args[args.length - 2] as (...args: NonNullableElements<T>) => K;
  }

  let callargs = elseFn ? args.slice(0, -2) : args.slice(0, -1);

  // Check if all arguments except the last (the function) are defined
  if (callargs.every(arg => arg !== undefined)) {
      // Call the function with the arguments, casting them to non-nullable types
      return thenFn(...(args.slice(0, -1) as NonNullableElements<T>));
  }
  if (elseFn)
    return elseFn();
  return undefined;
}

export function getInitials(userName: string) {
  const [name = '', surname = ''] = userName.split(/\s+/);
  let initials = '';
  if (name.length)
    initials += name[0];
  if (surname.length)
    initials += surname[0];
  return initials;
}
